import React, { useContext, useEffect, useMemo, useState } from "react";
import { DesignerProviderValue, PhoenixFormsDesignerContext } from ".";
import { Section } from "interfaces/Phoenix.FormsDesigner.Models";
import { classes } from "components/Utility";
import IconButton from "components/Shared/IconButton";
import { Button } from "components/Shared";
import ReorderableGrid, { ReorderableItem } from "components/Shared/ReorderableGrid";
import { stopPropagationPreventDefaultNoFn } from "tools/clickEvents";
import DesignerFormControl, { reorderQuestionHandle } from "./DesignerFormControl";
import { TextField } from "react-md";

const reorderSectionHandle = "reorder-section-handle";

interface DesignerSectionProps {
  section: Section;
  sectionIndex: number;
  isCollapsed: boolean;
  setCollapsed: (newValue: boolean) => void;
  setAllCollapsed: (newValue: boolean) => void;
  areAllCollapsed: boolean;
};

const DesignerSection = ({ section, isCollapsed, setCollapsed, setAllCollapsed, areAllCollapsed }: DesignerSectionProps) => {
  let context = useContext<DesignerProviderValue>(PhoenixFormsDesignerContext);
  const sectionIndex = context.formDefinition.pages[0].sections.indexOf(section);
  const [newSectionTitle, setNewSectionTitle] = useState<string>();
  const [isRenamingSection, setIsRenamingSection] = useState<boolean>();

  const controls = useMemo<ReorderableItem[]>(
    () => section.mobileQuestions.map((qid, i) => {
      
      let question = context.getQuestionById(qid);
      let nextQuestion = context.getQuestionById(section.mobileQuestions[i + 1]);

      let isStartOfQuestionMerge = !question.control.mergeWithAbove && nextQuestion?.control.mergeWithAbove;
      let isEndOfQuestionMerge = question.control.mergeWithAbove && !nextQuestion?.control.mergeWithAbove;

      return {
        id: qid,
        node: <DesignerFormControl
                key={qid}
                question={context.getQuestionById(qid)}
                parentSection={section}
                className={classes((question.control.mergeWithAbove || isStartOfQuestionMerge) && "merged-question", isStartOfQuestionMerge && "start-merged-question", isEndOfQuestionMerge && "end-merged-question")}
               />,
      };
    }),
    [context.formDefinitionUpdatedAt]);

  const editSectionName = () => {
    setIsRenamingSection(true);
    setNewSectionTitle(section.title);
  };

  const cancelEditingSectionName = () => {
    setIsRenamingSection(false);
  };

  const saveSectionName = () => {
    context.editFns.renameSection(sectionIndex, newSectionTitle);
    cancelEditingSectionName();
  };
  
  let titleNode = (
    <>
      <IconButton icon="edit" onClick={editSectionName}/>
      <span>{section.title}</span>
    </>
  );

  if (isRenamingSection) {
    titleNode = (
      <>
        <IconButton icon="clear" onClick={cancelEditingSectionName}/>
        <IconButton icon="check" onClick={saveSectionName}/>
        <span><TextField value={newSectionTitle} onChange={(v: string) => setNewSectionTitle(v)} /></span>
      </>
    );
  }

  return (
    <div className="form-section">
      <div className="section-title">
        { areAllCollapsed && <IconButton icon="reorder" className={reorderSectionHandle} onClick={stopPropagationPreventDefaultNoFn}/> }
        <IconButton icon="keyboard_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setCollapsed(!isCollapsed)}/>
        <IconButton icon="keyboard_double_arrow_down" className={classes("md-collapser", !isCollapsed && "md-collapser--flipped")} onClick={() => setAllCollapsed(!isCollapsed)}/>
        {titleNode}
        <IconButton icon="delete" onClick={() => context.editFns.deleteSection(sectionIndex)}/>
      </div>
      {!isCollapsed && <>
        <ReorderableGrid
          className="section-controls"
          items={controls}
          handleSelector={`.${reorderQuestionHandle}`}
          onReorder={ids => context.editFns.reorderQuestions({ section }, ids)}
        />
        <div className="new-question-container">
          <Button
            primary
            onClick={() => context.editFns.addNewQuestion({ section })}
          >
            Add New Question
          </Button>
          <Button
            primary
            onClick={() => context.editFns.addNewSection(sectionIndex)}
          >
            Add New Section
          </Button>
        </div>
        </>}
      </div>
    );
};

const PhoenixFormDesignerPreview = () => {
  let context = useContext<DesignerProviderValue>(PhoenixFormsDesignerContext);

  const sections = context.formDefinition.pages[0].sections;

  const [sectionCollapseStatus, _setSectionCollapseStatus] = useState<boolean[]>(() => sections.map(_ => false));
  
  useEffect(() => {
    // if we add a new section, update the sections collapse list
    if (context.lastChangeType === "add section" || context.lastChangeType === "delete section") {
      let newSections = [ ...sectionCollapseStatus ];
      console.log("last change data " + context.lastChangeData);
      if (context.lastChangeType === "add section") newSections.splice(context.lastChangeData as number + 1, 0, false);
      if (context.lastChangeType === "delete section") newSections.splice(context.lastChangeData as number, 1);
      _setSectionCollapseStatus(newSections);
    }
  }, [context.formDefinitionUpdatedAt]);

  const setCollapseStatus = (sectionIndex: number, newValue: boolean) => {
    let newStatus = [ ...sectionCollapseStatus ];
    newStatus[sectionIndex] = newValue;
    _setSectionCollapseStatus(newStatus);
  };

  const setAllCollapseStatus = (newValue: boolean) => {
    let newStatus = [ ...sectionCollapseStatus ];
    sections.forEach((_, i) => newStatus[i] = newValue);
    _setSectionCollapseStatus(newStatus);
  };

  const areAllCollapsed = !sectionCollapseStatus.some(s => !s);

  const controls = useMemo<ReorderableItem[]>(
    () => sections.map((s, i) => ({
      id: `${i}`,
      node: (
        <DesignerSection
          key={s.mobileQuestions[0] ?? s.title} // trying so hard to get a proper id here. i dont want to just refresh sections all the time, but i also dont have any real ids to go off of
          section={s}
          sectionIndex={i}
          isCollapsed={sectionCollapseStatus[i]}
          setCollapsed={val => setCollapseStatus(i, val)}
          setAllCollapsed={setAllCollapseStatus}
          areAllCollapsed={areAllCollapsed}
        />
      ),
    })),
    [context.formDefinitionUpdatedAt, sectionCollapseStatus]);

  return (
    <div className="phoenixform-designer-preview">
      <ReorderableGrid
        className="reorderable-sections"
        items={controls}
        handleSelector={`.${reorderSectionHandle}`}
        onReorder={ids => context.editFns.reorderSections(ids.map(i => Number.parseInt(i)))}
      />
      {/* Button outside of sections to allow for a new section when they are all collapsed */}
      {(!sectionCollapseStatus.length || sectionCollapseStatus[sections.length - 1]) && (
        <div className="bottom-new-section-button">
          <Button
            primary
            onClick={() => context.editFns.addNewSection(sections.length - 1)}
          >
            Add New Section
          </Button>
        </div>
      )}
    </div>
  );
};

export default PhoenixFormDesignerPreview;
